/**
 * Add Landlord Slide-over Alpine.js Component
 *
 * A create-only slideover for quickly adding new landlords.
 * Mirrors the add-property slideover pattern.
 */

export function addLandlordSlideover() {
  return {
    open: false,
    saving: false,
    formErrors: {},
    duplicateWarning: null,
    zIndex: 100010,

    form: {
      landlord_type: 'individual',
      title: '',
      first_name: '',
      last_name: '',
      display_name: '',
      organisation_name: '',
      email: '',
      cc_email_1: '',
      cc_email_2: '',
      cc_email_3: '',
      phone: '',
      phone_2: '',
      phone_3: '',
      address_line_1: '',
      address_line_2: '',
      town: '',
      county: '',
      postcode: '',
      country: '',
      bank_name: '',
      account_name: '',
      account_number: '',
      sort_code: '',
      iban_code: ''
    },

    init() {
      const self = this
      window.addEventListener('open-add-landlord-slideover', () => {
        self.openForCreate()
      })
    },

    openForCreate() {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.formErrors = {}
      this.duplicateWarning = null
      this.saving = false

      this.form = {
        landlord_type: 'individual',
        title: '',
        first_name: '',
        last_name: '',
        display_name: '',
        organisation_name: '',
        email: '',
        cc_email_1: '',
        cc_email_2: '',
        cc_email_3: '',
        phone: '',
        phone_2: '',
        phone_3: '',
        address_line_1: '',
        address_line_2: '',
        town: '',
        county: '',
        postcode: '',
        country: '',
        bank_name: '',
        account_name: '',
        account_number: '',
        sort_code: '',
        iban_code: ''
      }

      this.open = true
    },

    close() {
      this.open = false
    },

    get headerTitle() {
      const t = this.form.landlord_type
      if (t === 'organisation' && this.form.organisation_name.trim()) return this.form.organisation_name.trim()
      if (t === 'joint' && this.form.display_name.trim()) return this.form.display_name.trim()
      if (t === 'individual') {
        const parts = [this.form.first_name, this.form.last_name].filter(p => p && p.trim())
        if (parts.length > 0) return parts.join(' ')
      }
      return 'New Landlord'
    },

    // Has the user entered enough to identify a landlord (name + email)?
    get canCheckDuplicate() {
      if (!this.form.email.trim()) return false
      const t = this.form.landlord_type
      if (t === 'individual') return !!(this.form.first_name.trim() && this.form.last_name.trim())
      if (t === 'joint') return !!this.form.display_name.trim()
      if (t === 'organisation') return !!this.form.organisation_name.trim()
      return false
    },

    // Proactively warn the user, before they save, if a landlord with the same
    // name + email already exists. Debounced from the name/email inputs.
    checkDuplicate() {
      this.duplicateWarning = null
      if (!this.canCheckDuplicate) return

      const params = new URLSearchParams({
        landlord_type: this.form.landlord_type,
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        display_name: this.form.display_name,
        organisation_name: this.form.organisation_name,
        email: this.form.email
      })

      fetch(`/landlords/check_duplicate.json?${params.toString()}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(response => response.json())
        .then(data => {
          if (data.duplicate) this.duplicateWarning = data.landlord
        })
        .catch(error => {
          console.error('Duplicate check error:', error)
        })
    },

    openExisting() {
      const id = this.duplicateWarning?.id
      if (!id) return
      this.close()
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-landlord-slideover', {
          detail: { id }
        }))
      }, 300)
    },

    validateForm() {
      this.formErrors = {}
      const t = this.form.landlord_type

      if (t === 'individual') {
        if (!this.form.first_name.trim()) this.formErrors.first_name = 'Required'
        if (!this.form.last_name.trim()) this.formErrors.last_name = 'Required'
      } else if (t === 'joint') {
        if (!this.form.display_name.trim()) this.formErrors.display_name = 'Required'
      } else if (t === 'organisation') {
        if (!this.form.organisation_name.trim()) this.formErrors.organisation_name = 'Required'
      }

      if (Object.keys(this.formErrors).length > 0) {
        this.formErrors.base = 'Please fill in the required fields.'
        return false
      }
      return true
    },

    buildPayload() {
      return {
        landlord: {
          landlord_type: this.form.landlord_type,
          title: this.form.title || null,
          first_name: this.form.first_name.trim(),
          last_name: this.form.last_name.trim(),
          display_name: this.form.display_name.trim(),
          organisation_name: this.form.organisation_name.trim(),
          email: this.form.email.trim(),
          cc_email_1: this.form.cc_email_1.trim(),
          cc_email_2: this.form.cc_email_2.trim(),
          cc_email_3: this.form.cc_email_3.trim(),
          phone: this.form.phone.trim(),
          phone_2: this.form.phone_2.trim(),
          phone_3: this.form.phone_3.trim(),
          address_line_1: this.form.address_line_1.trim(),
          address_line_2: this.form.address_line_2.trim(),
          town: this.form.town.trim(),
          county: this.form.county.trim(),
          postcode: this.form.postcode.trim(),
          country: this.form.country.trim(),
          bank_name: this.form.bank_name.trim(),
          account_name: this.form.account_name.trim(),
          account_number: this.form.account_number.trim(),
          sort_code: this.form.sort_code.trim(),
          iban_code: this.form.iban_code.trim()
        }
      }
    },

    async createLandlord() {
      // Cannot create a landlord that duplicates an existing one by name + email.
      if (this.duplicateWarning) return
      if (!this.validateForm()) return

      this.saving = true

      try {
        const response = await fetch('/landlords.json', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(this.buildPayload())
        })

        const data = await response.json()

        if (response.ok && data.success) {
          window.dispatchEvent(new CustomEvent('landlord-created', {
            detail: { landlord: data.landlord }
          }))
          this.close()

          // Open the new landlord's detail slideover
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('open-landlord-slideover', {
              detail: { id: data.landlord.id }
            }))
          }, 300)
        } else {
          if (data.errors && typeof data.errors === 'object') {
            Object.entries(data.errors).forEach(([field, messages]) => {
              this.formErrors[field] = Array.isArray(messages) ? messages[0] : messages
            })
            // Preserve a server-supplied base message (e.g. the duplicate guard)
            // rather than overwriting it with the generic fallback.
            if (!this.formErrors.base) {
              this.formErrors.base = 'Please fix the errors and try again.'
            }
          } else {
            this.formErrors.base = 'Failed to create landlord'
          }
        }
      } catch (error) {
        console.error('Failed to create landlord:', error)
        this.formErrors.base = 'An unexpected error occurred. Please try again.'
      } finally {
        this.saving = false
      }
    }
  }
}
